import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Update as UpdateIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const TransactionDateFixer = () => {
  const [fixing, setFixing] = useState(false);
  const [progress, setProgress] = useState(0);

  const fixTransactionDates = async () => {
    setFixing(true);
    setProgress(0);
    
    try {
      // Get all recovered transactions (those with recovery_note)
      const transactionsQuery = query(
        collection(DATABASE, "transection_requests"),
        where("recovery_note", "!=", null)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const ledgersQuery = query(
        collection(DATABASE, "ledgers"),
        where("recovery_note", "!=", null)
      );
      const ledgersSnapshot = await getDocs(ledgersQuery);
      
      const batch = writeBatch(DATABASE);
      let updateCount = 0;
      
      // Update transaction dates based on their doc_id timestamp
      transactionsSnapshot.docs.forEach((docSnapshot, index) => {
        const data = docSnapshot.data();
        const docId = docSnapshot.id;
        
        // Extract date from doc_id (format: userId_YYYYMMDDHHmmss)
        const dateMatch = docId.match(/_(\d{14})$/);
        if (dateMatch) {
          const dateString = dateMatch[1];
          const year = dateString.substring(0, 4);
          const month = dateString.substring(4, 6);
          const day = dateString.substring(6, 8);
          const hour = dateString.substring(8, 10);
          const minute = dateString.substring(10, 12);
          const second = dateString.substring(12, 14);
          
          const correctDate = new Date(year, month - 1, day, hour, minute, second);
          
          batch.update(docSnapshot.ref, {
            date: Timestamp.fromDate(correctDate),
            created_at: Timestamp.fromDate(correctDate),
            request_approval_time: Timestamp.fromDate(correctDate),
          });
          
          updateCount++;
        }
        
        setProgress((index / transactionsSnapshot.docs.length) * 50);
      });
      
      // Update ledger dates based on their doc_id timestamp
      ledgersSnapshot.docs.forEach((docSnapshot, index) => {
        const data = docSnapshot.data();
        const docId = docSnapshot.id;
        
        // Extract date from doc_id (format: userId_YYYYMMDDHHmmss)
        const dateMatch = docId.match(/_(\d{14})$/);
        if (dateMatch) {
          const dateString = dateMatch[1];
          const year = dateString.substring(0, 4);
          const month = dateString.substring(4, 6);
          const day = dateString.substring(6, 8);
          const hour = dateString.substring(8, 10);
          const minute = dateString.substring(10, 12);
          const second = dateString.substring(12, 14);
          
          const correctDate = new Date(year, month - 1, day, hour, minute, second);
          
          batch.update(docSnapshot.ref, {
            date: Timestamp.fromDate(correctDate),
            created_at: Timestamp.fromDate(correctDate),
          });
          
          updateCount++;
        }
        
        setProgress(50 + (index / ledgersSnapshot.docs.length) * 50);
      });
      
      await batch.commit();
      setProgress(100);
      
      Swal.fire({
        icon: "success",
        title: "Transaction Dates Fixed!",
        html: `
          <div>
            <p><strong>Successfully updated ${updateCount} records</strong></p>
            <p>Transaction dates have been corrected to match their original closure dates.</p>
            <p>The Flutter app should now display transactions in the correct chronological order.</p>
          </div>
        `,
        width: 500,
      });
      
    } catch (error) {
      console.error("Error fixing transaction dates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to fix transaction dates: ${error.message}`,
      });
    } finally {
      setFixing(false);
      setProgress(0);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Transaction Date Fixer
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fix the chronological ordering of recovered transactions by updating their dates to match their original closure dates.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Issue:</strong> Recovered transactions are appearing at the end of the list instead of in chronological order. 
          This tool will fix the date fields to ensure proper sorting in the Flutter app.
        </Typography>
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Problem
              </Typography>
              <Typography variant="body2">
                Recovered transactions use current timestamp instead of original closure date, causing incorrect chronological ordering.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                Solution
              </Typography>
              <Typography variant="body2">
                Extract original dates from document IDs and update all date fields to match the original closure dates.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UpdateIcon />}
          onClick={fixTransactionDates}
          disabled={fixing}
          size="large"
        >
          {fixing ? "Fixing Dates..." : "Fix Transaction Dates"}
        </Button>
      </Box>

      {fixing && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Progress: {Math.round(progress)}%
          </Typography>
          <CircularProgress variant="determinate" value={progress} />
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>What this tool does:</strong><br/>
          • Finds all recovered transactions (those with recovery_note)<br/>
          • Extracts original dates from document IDs<br/>
          • Updates date, created_at, and request_approval_time fields<br/>
          • Ensures proper chronological ordering in Flutter app
        </Typography>
      </Alert>
    </Box>
  );
};

export default TransactionDateFixer;
